import { FC, useState, useLayoutEffect, useRef } from "react";
import { Rnd } from "react-rnd";
import type { RndResizeCallback } from "react-rnd";
import { getAspectRatio, getCamera } from "../../redux/reducers/ui/uiSelectors";
import {
  setAspectRatio,
  setWrapperDimensions,
} from "../../redux/reducers/ui/uiReducer";
import { ScreenDimensionsService } from "../../services/ScreenDimensionsService";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import type { resolutionListT } from "../../utils/constants";
import { setCameraFOV } from "../../utils/functions/playcanvas/setCameraFOV";
import { isPlayCanvasReady } from "../../utils/functions/playcanvas/isPlayCanvasReady";

const screenService = new ScreenDimensionsService();

export interface ResizableFrameProps {
  className?: string;
  showMask?: boolean;
}

export const ResizableFrame: FC<ResizableFrameProps> = ({
  className,
  showMask = true,
}) => {
  const dispatch = useAppDispatch();
  const aspectRatio = useAppSelector(getAspectRatio);
  const { fov } = useAppSelector(getCamera);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperWH, setWrapperWH] = useState({ w: 0, h: 0 });
  // Local frame state (position and size)
  const [frameState, setFrameState] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [isResizing, setIsResizing] = useState(false);
  // Manual frame size captured on resize stop for proportional scaling
  const [manualFrameSize, setManualFrameSize] = useState({
    width: 0,
    height: 0,
  });
  // Saved manual width proportion of the wrapper for custom scaling
  const [manualWidthPct, setManualWidthPct] = useState(1);
  // Margin in pixels to keep frame from touching wrapper edges
  const MARGIN = 8;
  const MAX_FOV = 170;

  useLayoutEffect(() => {
    const update = () => {
      if (wrapperRef.current) {
        const w = wrapperRef.current.offsetWidth;
        const h = wrapperRef.current.offsetHeight;
        setWrapperWH({ w, h });
        dispatch(setWrapperDimensions({ w, h }));
      }
    };
    update();
    let ro: ResizeObserver | null = null;
    if (wrapperRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(wrapperRef.current);
    }
    return () => ro?.disconnect();
  }, [dispatch]);

  // Removed debug frame size logging on restore lifecycle

  // Initialize or update frameState when wrapper size or aspect changes
  useLayoutEffect(() => {
    if (
      !wrapperRef.current ||
      !aspectRatio ||
      wrapperWH.w === 0 ||
      wrapperWH.h === 0
    )
      return;

    if (aspectRatio.id >= 0) {
      const { frame, pos } = screenService.fitFrameToWrapper(
        wrapperWH,
        aspectRatio,
        MARGIN
      );
      setFrameState({
        x: pos.x,
        y: pos.y,
        width: frame.width,
        height: frame.height,
      });

      if (isPlayCanvasReady()) {
        if (frame.width > 0 && frame.height > 0) {
          const referenceWidth = wrapperRef.current?.offsetWidth ?? wrapperWH.w;
          const actualFOVDegrees = screenService.calculateAdjustedFOV(
            fov,
            referenceWidth,
            frame.width,
            MAX_FOV
          );
          setCameraFOV(actualFOVDegrees);
        }
      }
    } else {
      // custom mode: prefer explicit saved custom size (aspectRatio.value) if manual is not initialized
      let frame, pos;
      const hasManual = manualFrameSize.width > 0 && manualFrameSize.height > 0;
      const hasSavedCustom = Boolean(
        (aspectRatio as any)?.value?.width &&
          (aspectRatio as any)?.value?.height
      );

      if (!hasManual && hasSavedCustom) {
        const targetW = Number((aspectRatio as any).value.width) || 0;
        const targetH = Number((aspectRatio as any).value.height) || 0;
        const maxW = Math.max(0, wrapperWH.w - MARGIN * 2);
        const maxH = Math.max(0, wrapperWH.h - MARGIN * 2);
        const width = Math.min(targetW, maxW);
        const height = Math.min(targetH, maxH);
        const x = Math.max(MARGIN, Math.round((wrapperWH.w - width) / 2));
        const y = Math.max(MARGIN, Math.round((wrapperWH.h - height) / 2));
        frame = { width, height };
        pos = { x, y };
        // seed manual for proportional scaling later
        setManualFrameSize({ width, height });
        setManualWidthPct(width / Math.max(1, wrapperWH.w));
      } else if (hasManual) {
        const scaled = screenService.scaleFrameToWrapper(
          manualFrameSize,
          wrapperWH,
          manualWidthPct,
          MARGIN
        );
        frame = scaled.frame;
        pos = scaled.pos;
      } else {
        const fitted = screenService.fitFrameToWrapper(
          wrapperWH,
          aspectRatio,
          MARGIN
        );
        frame = fitted.frame;
        pos = fitted.pos;
        setManualFrameSize({ width: frame.width, height: frame.height });
        setManualWidthPct(frame.width / Math.max(1, wrapperWH.w));
      }
      // apply state
      // (state update below logs INIT custom-aspect)

      const calc = { frame, pos };
      const { frame: _frame, pos: _pos } = calc;
      const frameObj = _frame;
      const posObj = _pos;
      setFrameState({
        x: posObj.x,
        y: posObj.y,
        width: frameObj.width,
        height: frameObj.height,
      });
      setFrameState({
        x: pos.x,
        y: pos.y,
        width: frame.width,
        height: frame.height,
      });

      if (isPlayCanvasReady()) {
        if (frame.width > 0 && frame.height > 0) {
          const referenceWidth = wrapperRef.current?.offsetWidth ?? wrapperWH.w;
          const actualFOVDegrees = screenService.calculateAdjustedFOV(
            fov,
            referenceWidth,
            frame.width,
            MAX_FOV
          );
          setCameraFOV(actualFOVDegrees);
        }
      }
    }
  }, [wrapperWH, aspectRatio, manualFrameSize, manualWidthPct]);

  // Apply initial adjusted FOV when PlayCanvas becomes ready (or immediately if already ready)
  useLayoutEffect(() => {
    const applyInitialFOV = async () => {
      if (!wrapperRef.current) return;
      const wrapW = wrapperRef.current?.offsetWidth ?? wrapperWH.w;
      let width = frameState.width;
      if ((width || 0) <= 0 && aspectRatio) {
        const { frame } = screenService.fitFrameToWrapper(
          wrapperWH,
          aspectRatio,
          MARGIN
        );
        width = frame.width;
      }
      if ((width || 0) > 0) {
        const actualFOVDegrees = screenService.calculateAdjustedFOV(
          fov,
          wrapW,
          width,
          MAX_FOV
        );
        const ok = await setCameraFOV(actualFOVDegrees, {
          retries: 20,
          delayMs: 250,
        });
        if (!ok) {
          console.warn(
            "[ResizableFrame] initial FOV apply failed, retrying once"
          );
          await setCameraFOV(actualFOVDegrees, { retries: 20, delayMs: 250 });
        }
      }
    };

    const onReady = () => {
      applyInitialFOV();
    };
    window.addEventListener("playcanvas-ready", onReady);
    // If already ready (fast reload), apply immediately
    // @ts-ignore
    if ((window as any).playCanvasReady) {
      applyInitialFOV();
    }
    return () => window.removeEventListener("playcanvas-ready", onReady);
  }, [aspectRatio, frameState.width, wrapperWH, fov]);

  // Re-apply FOV after configuration is loaded or scene changes
  useLayoutEffect(() => {
    const reapplyFOV = async () => {
      if (!wrapperRef.current) return;
      const wrapW = wrapperRef.current?.offsetWidth ?? wrapperWH.w;
      let width = frameState.width;
      if ((width || 0) <= 0 && aspectRatio) {
        const { frame } = screenService.fitFrameToWrapper(
          wrapperWH,
          aspectRatio,
          MARGIN
        );
        width = frame.width;
      }
      if ((width || 0) > 0) {
        const actualFOVDegrees = screenService.calculateAdjustedFOV(
          fov,
          wrapW,
          width,
          MAX_FOV
        );
        await setCameraFOV(actualFOVDegrees, { retries: 10, delayMs: 200 });
      }
    };
    const onConfigLoaded = () => {
      reapplyFOV();
    };
    const onSceneChanged = () => {
      reapplyFOV();
    };
    window.addEventListener("playcanvas-config-loaded", onConfigLoaded);
    window.addEventListener("playcanvas-scene-changed", onSceneChanged);
    return () => {
      window.removeEventListener("playcanvas-config-loaded", onConfigLoaded);
      window.removeEventListener("playcanvas-scene-changed", onSceneChanged);
    };
  }, [aspectRatio, frameState.width, wrapperWH, fov]);

  // Listen and apply exact frame size from template restore
  useLayoutEffect(() => {
    const onApplyFrameMeta = (e: any) => {
      const w = Number(e?.detail?.width) || 0;
      const h = Number(e?.detail?.height) || 0;
      if (!wrapperRef.current || w <= 0 || h <= 0) return;
      const wrapW = wrapperRef.current.offsetWidth;
      const wrapH = wrapperRef.current.offsetHeight;
      const width = Math.min(w, Math.max(0, wrapW - MARGIN * 2));
      const height = Math.min(h, Math.max(0, wrapH - MARGIN * 2));
      const x = Math.max(MARGIN, Math.round((wrapW - width) / 2));
      const y = Math.max(MARGIN, Math.round((wrapH - height) / 2));
      setManualFrameSize({ width, height });
      setManualWidthPct(width / Math.max(1, wrapW));
      setFrameState({ x, y, width, height });
      // debug log removed
    };
    window.addEventListener("apply-frame-meta", onApplyFrameMeta as any);
    return () =>
      window.removeEventListener("apply-frame-meta", onApplyFrameMeta as any);
  }, []);

  // Use local frameState (initialized and updated during drag)
  const { w, h } = wrapperWH;
  const { x, y, width: frameW, height: frameH } = frameState;

  // Update frameState continuously during drag, preserving center
  const handleResize: RndResizeCallback = (_e, _dir, ref, _delta, _pos) => {
    setIsResizing(true);
    const newW = ref.offsetWidth;
    const newH = ref.offsetHeight;

    const newFrameState = screenService.calculateResizedFrame(
      frameState,
      { width: newW, height: newH },
      wrapperWH,
      MARGIN
    );
    setFrameState(newFrameState);
    // debug log removed
    if (isPlayCanvasReady()) {
      // Use DOM offsetWidth for consistent reference
      const referenceWidth = wrapperRef.current?.offsetWidth ?? wrapperWH.w;
      const actualFOVDegrees = screenService.calculateAdjustedFOV(
        fov,
        referenceWidth,
        newFrameState.width,
        MAX_FOV
      );
      setCameraFOV(actualFOVDegrees);
    }
  };

  // On resize stop, update aspect ratio in store
  const handleResizeStop: RndResizeCallback = (_e, _d, ref, _delta, _pos) => {
    setIsResizing(false);
    const newW = ref.offsetWidth;
    const newH = ref.offsetHeight;

    const finalFrameState = screenService.calculateResizedFrame(
      frameState, // Use the latest frameState which might have been updated by handleResize
      { width: newW, height: newH },
      wrapperWH,
      MARGIN
    );
    setFrameState(finalFrameState);

    // Dispatch exact aspect ratio based on user-resized dimensions
    const customAspect: resolutionListT = {
      value: { width: finalFrameState.width, height: finalFrameState.height },
      label: `${finalFrameState.width}x${finalFrameState.height}`,
      id: -1,
    };
    dispatch(setAspectRatio(customAspect));
    // record manual frame size and width proportion for proportional scaling
    setManualFrameSize({
      width: finalFrameState.width,
      height: finalFrameState.height,
    });
    setManualWidthPct(finalFrameState.width / wrapperWH.w);
  };

  return (
    <div
      ref={wrapperRef}
      className={`${className ?? ""} resizable-frame-wrapper`}
      style={{
        width: "100%",
        height: "100%",
        pointerEvents: isResizing ? "auto" : "none",
      }}
    >
      {/* Resizable overlay for adjusting aspect ratio */}
      <Rnd
        size={{ width: frameState.width, height: frameState.height }}
        position={{ x: frameState.x, y: frameState.y }}
        bounds="parent"
        disableDragging
        lockAspectRatio={false}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        enableResizing={{
          top: true,
          right: true,
          bottom: true,
          left: true,
          topRight: true,
          bottomRight: true,
          bottomLeft: true,
          topLeft: true,
        }}
        resizeHandleStyles={{
          top: { pointerEvents: "all", cursor: "ns-resize" },
          right: { pointerEvents: "all", cursor: "ew-resize" },
          bottom: { pointerEvents: "all", cursor: "ns-resize" },
          left: { pointerEvents: "all", cursor: "ew-resize" },
          topRight: { pointerEvents: "all", cursor: "nesw-resize" },
          bottomRight: { pointerEvents: "all", cursor: "nwse-resize" },
          bottomLeft: { pointerEvents: "all", cursor: "nesw-resize" },
          topLeft: { pointerEvents: "all", cursor: "nwse-resize" },
        }}
        style={{
          boxSizing: "border-box",
          background: "transparent",
          pointerEvents: "none",
        }}
      />
      {/* Mask overlay with rounded inner corners */}
      {showMask && (
        <div
          style={{
            position: "absolute",
            top: y,
            left: x,
            width: frameW,
            height: frameH,
            borderRadius: 8,
            boxShadow: `0 0 0 ${Math.max(w, h)}px rgba(255, 255, 255, 0.20)`,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
};
